import _ from 'lodash'
import {
  usersCollection,
} from '@/firebase/data'
import {currentUserId} from '@/firebase/firebase'
import {transformDate, timestamp} from '@/firebase/firebaseHelpers'
import {daysFromDate, midnightToday, daysFromNow, isPast, getDateObject} from '@/helpers/date'
import {getCookie} from '@/helpers/cookie'
import trackAnalytics from '@/helpers/trackAnalytics'
import generateWelcomeBackMessage from '@/models/WelcomeBackMessages'
import generateAchievement from '@/models/Achievement'

// const getUserInfo = () => _.omit(
//   JSON.parse(localStorage.getItem('userInfo')) || {},
//   ['status', 'uid', 'about', 'userRole']
// )

const getTrialLength = (inviter) => {
  if (inviter === 'diplomka') {
    return 30
  } else if (inviter === 'diplomova_prace') {
    return 60
  } else if (inviter === 'early_access') {
    return 60
  }  else {
    return _.sample([3, 5, 7, 10, 14])
  }
}
const getPriceVariant = (age, status) => {
  if (status === 'student') {
    return 'student'
  }

  return _.sample(['3_9', '2_9', 'bigMac', 'yearly'])
}

const getTimezone = () => {
  try {
    if (Intl) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  } catch (error) {
    console.error(error)
    return null
  }
}

async function getLocation () {
  try {
     return fetch('https://api.ipregistry.co/?key=px212a6iu4u8fpyj').then(function (response) {
          return response.json()
      }).then(function (payload) {
          return { country: payload.location.country.name, city: payload.location.city }
      })
  } catch (error) {
    console.error(error)
    return null
  }
}

export default {
  FETCH_USER ({ commit }) {
    return new Promise((resolve, reject) => {
      usersCollection.doc(currentUserId()).get()
        .then((doc) => {
          const user = doc.data()
          if (user) {
            user.lastLoginAt = transformDate(user.lastLoginAt)
            user.lastSessionAt = transformDate(user.lastSessionAt)
            user.registeredAt = transformDate(user.registeredAt)
            user.lastActiveDay = transformDate(user.lastActiveDay)
            user.activeDaysStreak = user.activeDaysStreak || []
            // user.finishedMonthToMonthWizardPeriods = _.map((user.finishedMonthToMonthWizardPeriods || []), transformDate)
            user.academyRequested = user.academyRequested || false
            user.additionalInfo = user.additionalInfo || {}
            user.ratedQuotes = user.ratedQuotes || {}
            user.ratedTips = user.ratedTips || {}
            user.ratedCommunityTips = user.ratedCommunityTips || {}
            user.firstChapter = user.firstChapter || null
            user.membership = user.membership || { tarif: 'pretrial', endsOn: null }
            user.stripeCustomerId = user.stripeCustomerId || null
            user.backgroundImageName = user.backgroundImageName || null
            user.filter = { color: null, ...user.filter }
          }
          commit('SET_USER', user)
          resolve(user)
        })
        .catch((error) => { reject(error) })
    })
  },
  async UPDATE_USER_AUTH ({ state, dispatch }, { userInfo = {} }) {
    console.log('UPDATE_USER_AUTH ACTION', userInfo)

    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    await dispatch('FETCH_USER')
    let user = Object.assign({}, state.user)

    if (userInfo.onboardingFinished) {
      user.onboardingFinished = true
      userInfo = _.omit(userInfo, 'onboardingFinished')
    }

    if (userInfo.whatAreYouTryingToSolve) {
      user.whatAreYouTryingToSolve = userInfo.whatAreYouTryingToSolve
      userInfo = _.omit(userInfo, 'whatAreYouTryingToSolve')
    }

    user = Object.assign(user, {
      userInfo: _.merge({}, user.userInfo, _.pickBy(userInfo), { isAnonymous: !!userInfo.isAnonymous }),
      registeredAt: user.registeredAt ? user.registeredAt : timestamp(),
      lastLoginAt: timestamp(),
      loginCount: (user.loginCount || 0) + 1,
      host: window.location.host,
      analytics: _.merge({}, {
        _ga: getCookie('_ga'),
        _gid: getCookie('_gid'),
      }, user.analytics),
    })

    await dispatch('UPDATE_USER', { user })
  },
  async UPDATE_USER_VISIT ({ commit, state, getters, dispatch }, payload) {
    console.log('UPDATE_USER_VISIT')
    const lastActiveDay = getters.user.lastActiveDay

    setTimeout(
      () => generateWelcomeBackMessage({ notify: payload.notify, getters, lastActiveDay }),
      3000
    )

    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    await dispatch('FETCH_USER')
    const user = Object.assign({}, state.user)

    if (process.env.NODE_ENV === 'development') {
      console.log('id', currentUserId())
      console.log(`%c https://console.firebase.google.com/u/0/project/improvee-dev/firestore/data/~2Fusers~2F${currentUserId()}`, 'background: #222; color: #bada55')
    } else {
      console.log('id', currentUserId())
      console.log(`%c https://console.firebase.google.com/u/0/project/life-coach-vue/firestore/data/~2Fusers~2F${currentUserId()}`, 'background: #222; color: #bada55')
    }

    const updateFields = {
      lastSessionAt: timestamp(),
      sessionCount: (user.sessionCount || 0) + 1,
    }

    if (!user.timezone) {
      updateFields.timezone = getTimezone()
    }

    if (!user.mobile) {
      updateFields.mobile = getters.isMobile
    }

    if (!user.location) {
      updateFields.location = await getLocation()
    }

    if (!user.rewardful) {
      updateFields.rewardful = window.Rewardful && window.Rewardful.referral
    }

    const updateActiveDays = !user.lastActiveDay || daysFromDate(user.lastActiveDay) >= 1
    if (updateActiveDays) {
      updateFields.activeDays = ((user.activeDays || 0) + 1)
      updateFields.activeDaysStreak = _.concat(user.activeDaysStreak || [], [midnightToday()])
      updateFields.lastActiveDay = midnightToday()
      trackAnalytics(null, 'user', 'active_days', user.activeDays)
    }

    // create trial membership
    if ((!user.membership || (user.membership && user.membership.tarif === 'pretrial')) && user.userInfo && user.userInfo.email) {
      const trialLength = getTrialLength(getters.inviter)
      updateFields.membership = { tarif: 'trial', beginsOn: midnightToday(), endsOn: daysFromNow(trialLength), trialLength }
      updateFields.trialMembership = {...updateFields.membership}
      updateFields.priceVariant = getPriceVariant(getters.userAge, getters.userStatus)
      trackAnalytics(null, 'trial', 'success')
    }

    if (user.membership && user.membership.endsOn && user.membership.tarif === 'trial' && isPast(getDateObject(user.membership.endsOn))) {
      updateFields.membership = { tarif: 'expired', expiredOn: midnightToday() }
    }

    const sessionId = _.get(user.membership, 'sessionId')
    if (!user.stripeCustomerId && sessionId) {
      dispatch('FETCH_AND_STORE_STRIPE_CUSTOMER_ID', { sessionId })
    }

    generateAchievement({ user, dispatch, activeDays: updateFields.activeDays })
    commit('SET_LAST_REGISTRATION_AT', { date: new Date().toDateString() })
    await dispatch('UPDATE_USER_PARTIALLY', updateFields)
  },
  async UPDATE_USER_ONBOARDED ({ state, dispatch }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    await dispatch('FETCH_USER')
    let user = Object.assign({}, state.user)
    user = Object.assign(user, { onboardingFinished: true })
    await dispatch('UPDATE_USER', { user })
  },
  async SET_ACADEMY_REQUESTED ({ commit }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('SET_ACADEMY_REQUESTED', currentUserId())

    return usersCollection.doc(currentUserId()).update({ academyRequested: true }).then(() => {
      console.log('mutation SET_ACADEMY_REQUESTED')
      commit('SET_ACADEMY_REQUESTED')
    })
  },
  async ADMIN_RESET_SET_ACADEMY_REQUESTED ({ commit }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('SET_ACADEMY_REQUESTED', currentUserId())

    return usersCollection.doc(currentUserId()).update({ academyRequested: false }).then(() => {
      console.log('mutation SET_ACADEMY_REQUESTED')
      commit('SET_ACADEMY_REQUESTED')
    })
  },

  async ADMIN_RESET_ONBOARDING_FINISHED () {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    return usersCollection.doc(currentUserId()).update({ onboardingFinished: false }).then(() => {
      console.log('mutation ADMIN_RESET_ONBOARDING_FINISHED')
    })
  },
  async UPDATE_LANDBOT_MESSAGE_READ ({ commit }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    return usersCollection.doc(currentUserId()).update({ landbotMessage: false }).then(() => {
      commit('SET_LANDBOT_MESSAGE_READ')
    })
  },
  async UPDATE_USER_FCM_TOKEN ({ commit }, { fcm_token }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('UPDATE_USER_FCM_TOKEN', currentUserId(), fcm_token)

    return usersCollection.doc(currentUserId()).update({ fcm_token }).then(() => {
      commit('SET_FCM_TOKEN', { fcm_token })
    })
  },
  async ADDITIONAL_USER_INFO ({ state, commit }, info) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('ADDITIONAL_USER_INFO', info, state.user.additionalInfo)

    return usersCollection.doc(currentUserId()).set({ additionalInfo: info }, { merge: true }).then(() => {
      commit('SET_ADDITIONAL_INFO',  info)
    })
  },
  async QUOTE_RATED ({ state, commit }, { id, rating }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('SET_QUOTE_RATED', id, rating, state.user.ratedQuotes)

    const ratedObject = {}
    ratedObject[id] = rating

    return usersCollection.doc(currentUserId()).set({ ratedQuotes: ratedObject }, { merge: true }).then(() => {
      commit('SET_QUOTE_RATED', ratedObject)
    })
  },
  async TIP_RATED ({ state, commit }, { id, rating }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('SET_TIP_RATED', id, rating, state.user.ratedTips)

    const ratedObject = {}
    ratedObject[id] = rating

    return usersCollection.doc(currentUserId()).set({ ratedTips: ratedObject }, { merge: true }).then(() => {
      commit('SET_TIP_RATED', ratedObject)
    })
  },
  async COMMUNITY_TIP_RATED ({ state, commit }, { id, rating }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('SET_COMMUNITY_TIP_RATED', id, rating, state.user.ratedCommunityTips)

    const ratedObject = {}
    ratedObject[id] = rating

    return usersCollection.doc(currentUserId()).set({ ratedCommunityTips: ratedObject }, { merge: true }).then(() => {
      commit('SET_COMMUNITY_TIP_RATED', ratedObject)
    })
  },
  async CHANGE_BACKGROUND ({ commit }, { backgroundImageName }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('CHANGE_BACKGROUND', backgroundImageName)


    return usersCollection.doc(currentUserId()).set({ backgroundImageName }, { merge: true }).then(() => {
      commit('CHANGE_BACKGROUND', backgroundImageName)
    })
  },
  async CHANGE_FILTER ({ commit }, { filter }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('CHANGE_FILTER', filter)


    return usersCollection.doc(currentUserId()).set({ filter }, { merge: true }).then(() => {
      commit('CHANGE_FILTER', filter)
    })
  },
  async SET_FIRST_CHAPTER ({ commit }, firstChapter) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    console.log('SET_FIRST_CHAPTER', firstChapter)

    return usersCollection.doc(currentUserId()).set({ firstChapter }, { merge: true }).then(() => {
      commit('SET_FIRST_CHAPTER', firstChapter)
    })
  },
  async ADMIN_FIRST_CHAPTER_RESET ({ commit }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    return usersCollection.doc(currentUserId()).set({ firstChapter: null }, { merge: true }).then(() => {
      commit('SET_FIRST_CHAPTER', { firstChapter: null })
    })
  },

  async EXTEND_TRIAL ({ dispatch }, { whatIsMissing }) {
    const membership = {
      tarif: 'trial',
      trialExtendedOn: new Date().toDateString(),
      whatIsMissing
    }

    return new Promise((resolve, reject) => {
      usersCollection.doc(currentUserId()).update({ membership })
        .then((response) => {
          dispatch('FETCH_USER')
          resolve(response)
        })
        .catch((error) => { reject(error) })
    })
  },
  async UPDATE_USER_PARTIALLY ({ dispatch }, updateFields) {
    console.log('UPDATE_USER_PARTIALLY', updateFields)

    return new Promise((resolve, reject) => {
      usersCollection.doc(currentUserId()).update(updateFields)
        .then((response) => {
          dispatch('FETCH_USER')
          resolve(response)
        })
        .catch((error) => { reject(error) })
    })
  },
  async SUBSCRIPTION_PAID ({ dispatch }, { notify, period, sessionId }) {
    trackAnalytics(null, 'regular', 'success')

    const membership = {
      beginsOn: new Date(),
      tarif: 'regular',
      endsOn: daysFromNow(365),
      period,
      sessionId
    }

    notify({
      time: 15000,
      title: 'membership activated',
      text: 'You\'ve just taken an important step to improve your life.',
      iconPack: 'feather',
      icon: 'icon-sunrise',
      color: '#424CE2',
    })

    console.log('sessionId', sessionId)

    return new Promise((resolve, reject) => {
      usersCollection.doc(currentUserId()).update({ membership })
        .then((response) => {
          dispatch('FETCH_USER')
          dispatch('FETCH_AND_STORE_STRIPE_CUSTOMER_ID', {sessionId})
          resolve(response)
        })
        .catch((error) => { reject(error) })
    })
  },
  async SKIP_MONTH_TO_MONTH_FOR_NOW ({ commit }) {
    commit('HIDE_MONTH_TO_MONTH_WIZARD')
  },
  async UPDATE_USER_FINISHED_MONTH_TO_MONTH_WIZARD ({ commit, state }, { period }) {
    // if (process.env.VUE_APP_USER_ID || process.env.NODE_ENV === 'development') { return }

    const finishedMonthToMonthWizardPeriods = _.uniqBy(_.concat(state.user.finishedMonthToMonthWizardPeriods, [period]), (date) => date.toDateString())
    const showMonthToMonthWizard = false

    console.log('finishedMonthToMonthWizardPeriods', finishedMonthToMonthWizardPeriods)

    return new Promise((resolve, reject) => {
      usersCollection.doc(currentUserId()).update(
        { showMonthToMonthWizard, finishedMonthToMonthWizardPeriods }
      )
        .then((response) => {
          commit('HIDE_MONTH_TO_MONTH_WIZARD')
          resolve(response)
        })
        .catch((error) => { reject(error) })
    })

  },
  async UPDATE_USER ({ commit }, { user }) {
    if (process.env.VUE_APP_EXPLORATION_MODE) { return }

    return new Promise((resolve, reject) => {
      usersCollection.doc(currentUserId()).set(
        _.pickBy(user, _.identity)
      )
        .then((response) => {
          commit('SET_USER', user)
          resolve(response)
        })
        .catch((error) => { reject(error) })
    })
  },
  async FETCH_AND_STORE_STRIPE_CUSTOMER_ID ({ dispatch }, { sessionId }) {
    console.log('FETCH_AND_STORE_STRIPE_CUSTOMER_ID', sessionId)
    const session = await fetch(`/checkout-session?sessionId=${sessionId}`).then(r => r.json())

    console.log(session)

    if (session && session['customer']) {
      dispatch('UPDATE_USER_PARTIALLY', { stripeCustomerId: session['customer'] })
    }
  },
  async FORGET_USER ({ commit, dispatch }) {
    dispatch('UPDATE_USER_PARTIALLY', { forgetMe: true })
    commit('FORGET_USER')
  }
}
